import Navbar from "components/Navbar";
import React from "react";

import { CommonButton, CommonToast, UserInfoField } from "common/components";
import { ContentWrapper } from "components/ContentWrapper";
import { errorMessages } from "common/utils/validation";
import { ExperienceDetails } from "pages/kyc/subforms/RegisterUserExperienceSubform";
import { ForEach } from "common/utils/types";
import { Formik, Form } from "formik";
import { RegisterUserAccountsForm } from "pages/kyc/subforms";
import { StoreState } from "common/store/types";
import { UpdateUser } from "common/graphql/queryDescriptors";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { User } from "common/store/user/types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  Box,
  Grid,
  Typography,
  Card,
  CircularProgress,
} from "@material-ui/core";

import "./PersonalData.scss";

export const PersonalData = () => {
  //State
  const [toastOpen, setToastOpen] = React.useState(false);

  //Hooks
  const { t } = useTranslation();

  let { execute: executeUpdateUserDetails, loading } = useCustomQuery(
    UpdateUser({
      onSuccess: () => {
        setToastOpen(true);
      },
    })
  );

  const storageUserDetails = useSelector<StoreState, User>(
    (state) => state.currentUser
  );

  //Validation
  const onValidate = (values: User) => {
    const errors: any = new User(values).validate(
      Object.keys(values),
      errorMessages
    );

    return errors;
  };

  const onSubmit = async (values: User, { setSubmitting }: any) => {
    setSubmitting(true);

    const data = new User(values).updateRequest();

    await executeUpdateUserDetails({ variables: data });

    setSubmitting(false);
  };

  //Helpers
  const getClassName = (id: string) => {
    switch (id) {
      case "addressCountry":
      case "citizenship":
      case "idDocumentCountry":
      case "highestEducation":
      case "occupationGroup":
        return "autocomplete";
      default:
        return "";
    }
  };

  //Options
  const excludedFields = [
    "notUSTax",
    "experience",
    "transactionsPerYear",
    "volumePerYear",
  ];
  const displayedUserFields = User.addFields.filter(
    (field) => !excludedFields.includes(field)
  );

  return (
    <Box>
      <Navbar />

      <ContentWrapper>
        <Box className="personal-data">
          <Grid item className="section">
            <Typography className="section-title" variant="subtitle1">
              {t("Personal information")}
            </Typography>

            <Card className={"card"}>
              <Formik
                enableReinitialize
                validate={onValidate}
                onSubmit={onSubmit}
                initialValues={storageUserDetails}
              >
                {({
                  submitForm,
                  setFieldValue,
                  values,
                  errors,
                  touched,
                  isSubmitting,
                }) => {
                  const props = {
                    values,
                    errors,
                    touched,
                    setFieldValue,
                    disableImmutable: true,
                  };

                  return (
                    <Form autoComplete="off">
                      <Grid
                        container
                        direction="row"
                        spacing={4}
                        className="fields-container"
                      >
                        {displayedUserFields.map((field, i) => (
                          <Grid
                            item
                            xs={12}
                            md={6}
                            lg={4}
                            className="field-wrapper"
                            key={i}
                          >
                            <UserInfoField
                              id={field}
                              className={getClassName(field)}
                              {...props}
                            />
                          </Grid>
                        ))}
                      </Grid>

                      <Grid container spacing={5} direction="row">
                        {ForEach("ExperienceType", (x) => x).map((_, index) => (
                          <Grid xs={12} md={6} item key={index}>
                            <ExperienceDetails subkey={index} {...props} />
                          </Grid>
                        ))}
                      </Grid>

                      <Grid
                        container
                        item
                        direction="row"
                        className="button-container"
                      >
                        {loading || isSubmitting ? (
                          <CircularProgress />
                        ) : (
                          <CommonButton
                            onClick={submitForm}
                            isSubmitting={isSubmitting}
                            values={values}
                            errors={errors}
                            touched={touched}
                            text={t("Update")}
                          />
                        )}
                      </Grid>
                    </Form>
                  );
                }}
              </Formik>
            </Card>
          </Grid>

          <Grid item className="section">
            <Typography className="section-title">
              {t("Payment methods")}
            </Typography>

            <Card className={"card"}>
              <Box className="fields-container">
                <RegisterUserAccountsForm />
              </Box>
            </Card>
          </Grid>
          <CommonToast
            open={toastOpen}
            onClose={() => setToastOpen(false)}
            text={t("Changes saved!")}
            severity={"success"}
          />
        </Box>
      </ContentWrapper>
    </Box>
  );
};
